// src/context/AuthContext.js
import { createContext, useContext, useEffect, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db, authReady } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import { setMonitoringUser, captureMonitoringException } from "../services/monitoringService";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    // Safety timeout: Only as a last resort if Firebase never responds (15 seconds)
    const safetyTimeout = setTimeout(() => {
      if (!authChecked) {
        console.warn("Auth Listener timed out after 15s. Forcing loading=false.");
        setLoading(false);
        setAuthChecked(true);
      }
    }, 15000);

    let unsubscribe;

    // Wait for auth persistence to be ready BEFORE listening for auth state
    // This prevents a race condition where onAuthStateChanged fires with null
    // before the persisted session is restored on page refresh
    authReady.then(() => {
      try {
        unsubscribe = onAuthStateChanged(auth, async (user) => {
          clearTimeout(safetyTimeout);
          console.log("AuthProvider: Auth state changed. User:", user ? user.uid : "null");
          if (user) {
            try {
              const docRef = doc(db, "admins", user.uid);
              const docSnap = await getDoc(docRef);
              if (docSnap.exists()) {
                const adminUser = { ...user, ...docSnap.data() };
                setCurrentUser(adminUser);
                setMonitoringUser(adminUser);
              } else {
                // User exists in Firebase Auth but not in admins collection
                setCurrentUser(user);
                setMonitoringUser(user);
              }
            } catch (err) {
              console.error("Error fetching admin doc:", err);
              captureMonitoringException(err, {
                tags: { area: "admin_auth" },
                extra: { uid: user.uid },
              });
              // Still set the user even if Firestore fails — don't log them out
              setCurrentUser(user);
              setMonitoringUser(user);
            }
          } else {
            setCurrentUser(null);
            setMonitoringUser(null);
          }
          setAuthChecked(true);
          setLoading(false);
        }, (error) => {
          clearTimeout(safetyTimeout);
          console.error("Auth Listener Error:", error);
          captureMonitoringException(error, {
            tags: { area: "admin_auth_listener" },
          });
          setAuthChecked(true);
          setLoading(false);
        });
      } catch (e) {
        clearTimeout(safetyTimeout);
        console.error("Error initializing auth listener:", e);
        captureMonitoringException(e, {
          tags: { area: "admin_auth_init" },
        });
        setAuthChecked(true);
        setLoading(false);
      }
    }).catch((err) => {
      clearTimeout(safetyTimeout);
      console.error("Auth persistence error:", err);
      setAuthChecked(true);
      setLoading(false);
    });

    return () => {
      clearTimeout(safetyTimeout);
      if (unsubscribe) unsubscribe();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        color: 'black',
        fontSize: '20px',
        gap: '16px'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '4px solid #e5e7eb',
          borderTop: '4px solid #ef4444',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        Loading Admin Panel...
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ currentUser, authChecked }}>
      {children}
    </AuthContext.Provider>
  );
};

// optional hook
export const useAuth = () => useContext(AuthContext);
